import { useEffect, useRef } from 'react'

export interface HotkeySpec {
  /** Lowercase `event.key`, e.g. `'k'`, `'escape'`, `'/'`. */
  key: string
  /** Cmd on macOS, Ctrl elsewhere. */
  meta?: boolean
  shift?: boolean
  alt?: boolean
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * Binds a document-level shortcut. Shortcuts without a modifier are suppressed while the
 * user is typing, so `/` in a caption never opens a palette.
 */
export const useHotkey = (
  spec: HotkeySpec,
  handler: (event: KeyboardEvent) => void,
  enabled = true,
): void => {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const { key, meta = false, shift = false, alt = false } = spec

  useEffect(() => {
    if (!enabled) return

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== key) return
      const wantsModifier = meta
      const hasModifier = event.metaKey || event.ctrlKey
      if (wantsModifier !== hasModifier) return
      if (shift !== event.shiftKey) return
      if (alt !== event.altKey) return
      if (!wantsModifier && key !== 'escape' && isTypingTarget(event.target)) return
      event.preventDefault()
      handlerRef.current(event)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [key, meta, shift, alt, enabled])
}

/** `⌘` on Apple platforms, `Ctrl` everywhere else — used in shortcut hints. */
export const modifierLabel = (): string =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'
